import { useState } from 'react'
import { X, Save, AlertCircle, CheckCircle } from 'lucide-react'

interface OKRDetailModalProps {
  okr: {
    id: number
    objective_title: string
    objective_description: string
    target_value: number
    current_value: number
    unit: string
    status: string
    progress_percentage: number
    owner_name: string
    owner_email: string
    start_date: string
    end_date: string
    priority: string
  }
  isOpen: boolean
  onClose: () => void
  onSave: (updatedOkr: any) => Promise<void>
}

const statusOptions = [
  { value: 'on_track', label: 'On Track', color: 'green', icon: CheckCircle },
  { value: 'at_risk', label: 'At Risk', color: 'yellow', icon: AlertCircle },
  { value: 'off_track', label: 'Off Track', color: 'red', icon: AlertCircle },
  { value: 'completed', label: 'Completed', color: 'blue', icon: CheckCircle },
]

export default function OKRDetailModal({ okr, isOpen, onClose, onSave }: OKRDetailModalProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [formData, setFormData] = useState({
    current_value: okr.current_value,
    status: okr.status,
    objective_description: okr.objective_description,
  })
  const [error, setError] = useState<string | null>(null)

  if (!isOpen) return null

  const progress = Math.round((formData.current_value / okr.target_value) * 100)
  const daysRemaining = Math.ceil(
    (new Date(okr.end_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
  )

  const handleSave = async () => {
    setIsSaving(true)
    setError(null)

    // Validation
    if (formData.current_value < 0) {
      setError('Current value cannot be negative')
      setIsSaving(false)
      return
    }

    if (formData.current_value > okr.target_value * 1.5) {
      setError('Current value seems unusually high. Please verify.')
      setIsSaving(false)
      return
    }

    try {
      await onSave({
        id: okr.id,
        ...formData,
        progress_percentage: progress,
      })
      setIsEditing(false)
    } catch (err) {
      setError('Failed to save changes. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setFormData({
      current_value: okr.current_value,
      status: okr.status,
      objective_description: okr.objective_description,
    })
    setIsEditing(false)
    setError(null)
  }

  const currentStatus = statusOptions.find(s => s.value === formData.status) || statusOptions[0]
  const StatusIcon = currentStatus.icon

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl font-bold text-vampire-black">
                {okr.objective_title}
              </h2>
              <span className={`text-xs px-3 py-1 rounded-full font-semibold ${
                okr.priority === 'CRITICAL' ? 'bg-red-100 text-red-800 border border-red-300' :
                okr.priority === 'HIGH' ? 'bg-orange-100 text-orange-800 border border-orange-300' :
                okr.priority === 'MEDIUM-HIGH' ? 'bg-yellow-100 text-yellow-800 border border-yellow-300' :
                'bg-blue-100 text-blue-800 border border-blue-300'
              }`}>
                {okr.priority}
              </span>
            </div>
            <p className="text-sm text-graphite-grey">
              Owner: {okr.owner_name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-graphite-grey hover:text-vampire-black transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Progress Overview */}
          <div className="bg-pearl-white p-6 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-graphite-grey mb-1">Progress</p>
                <p className="text-4xl font-bold text-royal-blue">{progress}%</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-graphite-grey mb-1">Current / Target</p>
                <p className="text-2xl font-semibold text-vampire-black">
                  {formData.current_value} / {okr.target_value} {okr.unit}
                </p>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className={`h-3 rounded-full transition-all ${
                  progress >= 100 ? 'bg-green-500' :
                  progress >= 70 ? 'bg-blue-500' :
                  progress >= 30 ? 'bg-yellow-500' :
                  'bg-gray-400'
                }`}
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
          </div>

          {/* Timeline */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-graphite-grey mb-1">Start Date</p>
              <p className="font-semibold text-vampire-black">
                {new Date(okr.start_date).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p className="text-xs text-graphite-grey mb-1">End Date</p>
              <p className="font-semibold text-vampire-black">
                {new Date(okr.end_date).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p className="text-xs text-graphite-grey mb-1">Days Remaining</p>
              <p className={`font-semibold ${
                daysRemaining < 0 ? 'text-red-600' :
                daysRemaining < 7 ? 'text-yellow-600' :
                'text-green-600'
              }`}>
                {daysRemaining < 0 ? 'Overdue' : `${daysRemaining} days`}
              </p>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-vampire-black mb-2">
              Description
            </label>
            {isEditing ? (
              <textarea
                value={formData.objective_description}
                onChange={(e) => setFormData({ ...formData, objective_description: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-royal-blue focus:border-transparent"
                rows={3}
              />
            ) : (
              <p className="text-graphite-grey">{formData.objective_description}</p>
            )}
          </div>

          {/* Current Value */}
          <div>
            <label className="block text-sm font-semibold text-vampire-black mb-2">
              Current Value ({okr.unit})
            </label>
            {isEditing ? (
              <input
                type="number"
                value={formData.current_value}
                onChange={(e) => setFormData({ ...formData, current_value: parseFloat(e.target.value) })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-royal-blue focus:border-transparent"
                step="0.01"
                min="0"
              />
            ) : (
              <p className="text-2xl font-semibold text-royal-blue">
                {formData.current_value} {okr.unit}
              </p>
            )}
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-semibold text-vampire-black mb-2">
              Status
            </label>
            {isEditing ? (
              <div className="grid grid-cols-2 gap-3">
                {statusOptions.map((option) => {
                  const Icon = option.icon
                  return (
                    <button
                      key={option.value}
                      onClick={() => setFormData({ ...formData, status: option.value })}
                      className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                        formData.status === option.value
                          ? `border-${option.color}-500 bg-${option.color}-50`
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Icon className={`w-5 h-5 ${
                        formData.status === option.value
                          ? `text-${option.color}-600`
                          : 'text-gray-400'
                      }`} />
                      <span className="font-medium">{option.label}</span>
                    </button>
                  )
                })}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <StatusIcon className={`w-5 h-5 text-${currentStatus.color}-600`} />
                <span className={`font-semibold text-${currentStatus.color}-600`}>
                  {currentStatus.label}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-6 py-2 text-graphite-grey hover:text-vampire-black transition-colors"
          >
            Close
          </button>
          <div className="flex gap-3">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancel}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-6 py-2 bg-royal-blue text-white rounded-lg hover:bg-royal-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-4 h-4" />
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="px-6 py-2 bg-royal-blue text-white rounded-lg hover:bg-royal-blue-700 transition-colors"
              >
                Edit Progress
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
